import { getLocalTime } from '@gripp/shared-logic';

// Shared helpers for serializing routine submission answers into CSV cells.
// Extracted from downloadRoutine.tsx so the data-plate export hook can reuse
// them without depending on the routine-export component (avoids a cycle).

export const RESPONSE_VALUE_KEYS = [
  'value',
  'confirmedValue',
  'count',
  'status',
  'option',
  'options',
  'mediaIds',
  'timeTypeValue',
  'barcodes',
] as const;

export type ResponseValueKey = (typeof RESPONSE_VALUE_KEYS)[number];

// Response value keys whose answers are lists — these are the "multiples"
// (barcode scans, images, multi-selects) that split mode spreads across one
// column per value instead of comma-joining into a single cell. Single-value
// keys (option, value, status, …) are never split.
export const MULTI_VALUE_KEYS = new Set<ResponseValueKey>([
  'barcodes',
  'mediaIds',
  'options',
]);

export type BarcodeExportEntry = {
  value?: unknown;
  quantity?: unknown;
};

// react-csv 2.x wraps values in double-quotes but does NOT escape inner double-quotes,
// producing malformed CSV when values contain characters like inch marks (e.g. .010").
// Sanitize all string output to avoid breaking column alignment in the exported file.
export function sanitizeCsvString(value: string): string {
  return value.replace(/"/g, "'");
}

function formatSingleBarcode(entry: BarcodeExportEntry): string {
  if (!entry || typeof entry !== 'object') {
    return '';
  }

  const barcodeValue = typeof entry.value === 'string' ? entry.value : '';
  if (!barcodeValue) {
    return '';
  }

  return entry.quantity !== undefined
    ? `${barcodeValue} (${entry.quantity})`
    : barcodeValue;
}

function formatBarcodeEntries(value: BarcodeExportEntry[]): string {
  return sanitizeCsvString(
    value.map(formatSingleBarcode).filter(Boolean).join(', ')
  );
}

/**
 * Splits a single response into its individual scalar values for "split to rows"
 * mode. Multi-value answers (barcodes, images/mediaIds, multi-select options)
 * yield one entry each; single-value answers yield exactly one (reusing
 * formatResponseValue so timeType/open-ended formatting stays consistent).
 */
export function extractIndividualValues(
  response: Record<string, unknown>,
  valueKey?: ResponseValueKey
): string[] {
  if (!valueKey) {
    return [];
  }

  const value = response[valueKey];

  if (valueKey === 'barcodes' && Array.isArray(value)) {
    return value
      .map((entry) => formatSingleBarcode(entry as BarcodeExportEntry))
      .filter(Boolean)
      .map(sanitizeCsvString);
  }

  if (Array.isArray(value)) {
    return value
      .map((item) =>
        typeof item === 'string' ? sanitizeCsvString(item) : String(item)
      )
      .filter((item) => item !== '');
  }

  const formatted = formatResponseValue(response, valueKey);
  if (formatted === '' || formatted === undefined || formatted === null) {
    return [];
  }

  return [formatted as string];
}

export function getResponseKey(
  response: Record<string, unknown>
): ResponseValueKey | undefined {
  const keys = Object.keys(response);
  return keys.find((key): key is ResponseValueKey =>
    RESPONSE_VALUE_KEYS.includes(key as ResponseValueKey)
  );
}

export function formatResponseValue(
  response: Record<string, unknown>,
  valueKey?: ResponseValueKey
) {
  if (!valueKey) {
    return '';
  }

  const value = response[valueKey];

  if (valueKey === 'barcodes' && Array.isArray(value)) {
    return formatBarcodeEntries(value as BarcodeExportEntry[]);
  }

  if (Array.isArray(value)) {
    return sanitizeCsvString(value.join(', '));
  }

  if (value && valueKey === 'timeTypeValue') {
    // Each TimeType variation stores a distinguishable value shape, so we format
    // by shape to match the type the routine builder selected:
    //   - { startTime, endTime } (Start & end times) -> time only, e.g. "2:30 PM - 4:00 PM"
    //   - { date, time }         (Time & date)        -> "06/29/2026 2:30 PM"
    //   - "MM/DD/YYYY" string    (Date only)          -> raw date
    //   - ISO string             (Time only)          -> time only, e.g. "2:30 PM"
    if (typeof value === 'object') {
      if ('startTime' in value && 'endTime' in value) {
        return `${getLocalTime(
          (value as { startTime: string }).startTime
        )} - ${getLocalTime((value as { endTime: string }).endTime)}`;
      }
      if ('date' in value || 'time' in value) {
        const { date, time } = value as { date?: string; time?: string };
        return [date ?? '', time ? getLocalTime(time) : '']
          .filter(Boolean)
          .join(' ');
      }
    } else if (typeof value === 'string') {
      if (!/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
        return getLocalTime(value);
      }
      return value;
    }
  } else if (
    valueKey === 'status' &&
    value === 'openEndedResponse' &&
    response.openEndedResponse
  ) {
    return sanitizeCsvString(response.openEndedResponse as string);
  }

  if (typeof value === 'string') {
    return sanitizeCsvString(value);
  }

  return value;
}

// A single routine submission answer. Always carries a questionId; the rest of
// the shape depends on the question type (value/option/options/barcodes/…).
export type SubmissionResponse = { questionId: string } & Record<
  string,
  unknown
>;

// One row in the exported CSV, keyed by column key (base columns + questionId).
// Keys that are absent render as an empty cell (react-csv), which is how split
// mode blanks out every cell that isn't relevant to an extra row.
export type ExportRow = Record<string, unknown>;

/**
 * Builds the CSV row(s) for a single routine submission.
 *
 * Normally returns one row. In split-to-rows mode, each multi-value answer
 * (barcode scans, images, multi-select options) with more than one value
 * spreads DOWN: its first value stays on the submission's primary row and every
 * additional value becomes its own row below. Those extra rows contain ONLY the
 * relevant question's cell — the base columns, single-value questions, and any
 * shorter multi-value answers are intentionally omitted so they render blank.
 */
export function buildSubmissionRows(
  responses: SubmissionResponse[] | undefined,
  primaryRowSeed: ExportRow,
  options: {
    splitToRows: boolean;
    multiColumnCounterIds: Set<string>;
    formatCounterData: (response: SubmissionResponse) => Record<string, string>;
  }
): ExportRow[] {
  const { splitToRows, multiColumnCounterIds, formatCounterData } = options;

  const primaryRow: ExportRow = { ...primaryRowSeed };

  // Multi-value answers (>1 value) that spread down: questionId -> values.
  // Index 0 lives on the primary row; indexes 1..n become the extra rows.
  const spreadValuesByQid = new Map<string, string[]>();

  responses?.forEach((response) => {
    const qid = response.questionId;

    if (multiColumnCounterIds.has(qid)) {
      Object.assign(primaryRow, formatCounterData(response));
      return;
    }

    const valueKey = getResponseKey(response);

    if (splitToRows && valueKey && MULTI_VALUE_KEYS.has(valueKey)) {
      const values = extractIndividualValues(response, valueKey);
      if (values.length > 1) {
        spreadValuesByQid.set(qid, values);
        primaryRow[qid] = values[0];
        return;
      }
    }

    primaryRow[qid] = formatResponseValue(response, valueKey);
  });

  if (spreadValuesByQid.size === 0) {
    return [primaryRow];
  }

  // The submission needs as many rows as its longest multi-value answer.
  const rowCount = Math.max(
    ...Array.from(spreadValuesByQid.values(), (values) => values.length)
  );

  const rows: ExportRow[] = [primaryRow];
  for (let i = 1; i < rowCount; i++) {
    const extraRow: ExportRow = {};
    for (const [qid, values] of spreadValuesByQid) {
      if (i < values.length) {
        extraRow[qid] = values[i];
      }
    }
    rows.push(extraRow);
  }

  return rows;
}
